import {
  ActionMenu,
  DotMenuHorizontalIcon,
  BarChart,
  BodyMedium,
  BodySmall,
  cleanArray,
  getFrontendConfigValue,
  Tabs,
  theme,
  Tooltip,
} from '@arnold/common';
import { XAxisTickProps } from '@arnold/common/lib/components/charts/BarChart/BarChart';
import styled from '@emotion/styled';
import { FC, MouseEventHandler, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { DashboardQuery, OrganizationQuery } from '../../generated/hooks';
import { getShortenedTitle } from '../../lib/helpers/surveyGroup';
import { NoOnboardingsInfo } from '../NoSGInfo/NoOnbardingsInfo';
import { DashboardSection } from './DashboardSection';
import { getBarChartData, getSurveyGroup } from './utils';
import { isLoggedUserSysAdmin } from '../../lib/helpers';

const BarChartContainer = styled.div`
  background-color: ${theme.background.primary};
  padding: ${theme.spacing.g} ${theme.spacing.h};
  width: 100%;
`;

type OnboardingProps = {
  organization: NonNullable<OrganizationQuery['organization']>;
  surveyGroups: NonNullable<DashboardQuery['user']['organization']>['activeOnboadings'];
};

export const Onboarding: FC<OnboardingProps> = ({ surveyGroups, organization }) => {
  const { t } = useTranslation('dashboard');
  const { surveyGroupId } = useParams<{ surveyGroupId: string }>();
  const surveyGroup = getSurveyGroup(surveyGroups, surveyGroupId);
  const barChartContainerRef = useRef<HTMLDivElement>(null);

  const [selectedSurveyGroup, setSelectedSurveyGroup] = useState<
    NonNullable<DashboardQuery['user']['organization']>['activeOnboadings'][0] | undefined
  >(surveyGroups.length ? (surveyGroup ? surveyGroup : surveyGroups[0]) : undefined);
  const organizationEdition = organization.productVersion?.product;
  const selectedSurveyGroupIndex = surveyGroups.indexOf(selectedSurveyGroup!);
  const sortedSurveyGroups = [...surveyGroups];
  if (selectedSurveyGroupIndex > 2) {
    sortedSurveyGroups.splice(2, 0, sortedSurveyGroups.splice(selectedSurveyGroupIndex, 1)[0]);
  }

  const history = useHistory();

  useEffect(() => {
    if (surveyGroupId) {
      const surveyGroup = getSurveyGroup(surveyGroups, surveyGroupId);
      setSelectedSurveyGroup(surveyGroup);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyGroupId]);

  const barCharData = getBarChartData(selectedSurveyGroup);
  const topLevelReportAccess = selectedSurveyGroup?.topLevelReport;

  const chatSatisfactionScale =
    selectedSurveyGroup?.steps.map((step) => step.processStep.stepTopicGroup.chatSatisfactionScale || 0) || [];
  const maxScale = chatSatisfactionScale.length ? Math.max(...chatSatisfactionScale) : 0;

  const barOnClick = (y: number, topicGroupId: string) => {
    if (topLevelReportAccess && y) {
      window.location.href = `${getFrontendConfigValue('ARNOLD_URL')}/report/${
        topLevelReportAccess!.accessKey
      }?topic_group_id=${topicGroupId}${isLoggedUserSysAdmin() ? '&sysAdmin' : ''}`;
    } else {
      // empty onboarding without surveys do not have top level report access
      history.push(`/onboarding/${selectedSurveyGroup?.id}/topics`);
    }
  };

  return (
    <DashboardSection
      title={t('onboarding')}
      buttons={<></>}
      key={'onboarding'}
      keyName={'onboarding'}
      explanation={maxScale === 0 ? undefined : t('scaleExplanation', { max: maxScale })}
    >
      {surveyGroups?.length > 1 && (
        <Tabs
          items={cleanArray([
            ...sortedSurveyGroups.slice(0, 3).map((surveyGroup) => {
              return {
                title: getShortenedTitle(surveyGroup ? surveyGroup.name || surveyGroup.topicGroup.name || '' : ''),
                link: `/overview/${surveyGroup.id}`,
                active: surveyGroup.id === selectedSurveyGroup?.id,
              };
            }),
            surveyGroups.length > 3 && {
              title: (
                <ActionMenu
                  toggleIcon={<DotMenuHorizontalIcon />}
                  items={sortedSurveyGroups.slice(3).map((surveyGroup, i) => {
                    return {
                      label: getShortenedTitle(
                        surveyGroup ? surveyGroup.name || surveyGroup.topicGroup.name || '' : '',
                      ),
                      action: () => {
                        setSelectedSurveyGroup(surveyGroup);
                        history.push(`/overview/${surveyGroup.id}`);
                      },
                    };
                  })}
                />
              ),
            },
          ])}
          theme={theme}
        />
      )}
      {barCharData.length > 0 && (
        <BarChartContainer ref={barChartContainerRef}>
          <BarChart
            height={192}
            xAxisHeight={86}
            onClick={(chartData: { id: string; step: number; y: number; topicGroupId: string }) => {
              barOnClick(chartData.y, chartData.topicGroupId);
            }}
            data={barCharData}
            CustomXAxisTick={(props: XAxisTickProps) => (
              <svg x={props.x - 16} y={props.y} style={{ overflow: 'visible' }}>
                <foreignObject width="80" height="100" overflow="visible">
                  <div>
                    <BarAndLabelHoverEffect
                      payload={props.payload}
                      x={props.x}
                      y={props.y}
                      barChartContainerRef={barChartContainerRef}
                      onClick={() => {
                        const data = barCharData[props.payload.index];
                        barOnClick(data.y, data.topicGroupId);
                      }}
                    />
                    <BodyMedium color={theme.colors.text.primary} margin={'10px 0 10px 0'}>
                      {Math.round((barCharData.find((data) => data.x === props.payload.value)?.y ?? 0) * 10) / 10}
                    </BodyMedium>
                    <Tooltip title={props.payload.value}>
                      <BodySmall color={theme.colors.text.secondary} ellipsis={2}>
                        {props.payload.value}
                      </BodySmall>
                    </Tooltip>
                  </div>
                </foreignObject>
              </svg>
            )}
            hideYAxis
          />
        </BarChartContainer>
      )}
      {!surveyGroups.length && <NoOnboardingsInfo edition={organizationEdition} />}
    </DashboardSection>
  );
};

const BarAndLabelHoverCatcher = styled.div`
  position: absolute;
  width: 100px;
  height: 180px;
  bottom: 17px;
  left: -16px;
  z-index: 10;
  border-radius: ${theme.borderRadius.l};
  cursor: pointer;
`;

const BarAndLabelBackgroundRect = styled(BarAndLabelHoverCatcher)<{ hover: boolean }>`
  z-index: -1;
  ${({ hover }) => hover && `background-color: ${theme.colors.backgroundBasic.default};`}
`;

const BarAndLabelHoverEffect = (
  props: XAxisTickProps & {
    onClick: () => void;
    barChartContainerRef: RefObject<HTMLDivElement>;
  },
) => {
  const [hover, setHover] = useState(false);
  const hoverCatcherDiv = useRef<HTMLDivElement>(null);

  const onMouseOver = useCallback(() => {
    setHover(true);
    const barPath = getBarPath(props.barChartContainerRef, (props.payload as any).index);
    if (barPath) {
      barPath.style.filter = 'saturate(10) brightness(0.5)';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.barChartContainerRef]);

  const onMouseOut: MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (isInsideClientRect(hoverCatcherDiv.current!.getBoundingClientRect(), e.clientX, e.clientY)) {
        // If the mouse is still inside the hover catcher, don't remove the hover effect
        // This can happen if some SVG element is on top of the hover catcher (z-index does not work well with SVG)
        return;
      }
      setHover(false);
      const barPath = getBarPath(props.barChartContainerRef, (props.payload as any).index);
      if (barPath) {
        barPath.style.fill = null!;
        barPath.style.filter = null!;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.barChartContainerRef],
  );

  return (
    <>
      <BarAndLabelHoverCatcher
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
        onClick={() => props.onClick()}
        ref={hoverCatcherDiv}
      />
      <BarAndLabelBackgroundRect hover={hover} />
    </>
  );
};
const getBarPath = (barChartContainerRef: RefObject<HTMLDivElement>, index: number) => {
  if (barChartContainerRef.current) {
    const barPaths = barChartContainerRef.current.querySelectorAll(`.recharts-bar-rectangles path.barPath`);
    return barPaths[index] as SVGPathElement;
  }
};

const isInsideClientRect = (clientRect: ClientRect, x: number, y: number) => {
  return x > clientRect.left + 2 && x < clientRect.right - 2 && y > clientRect.top + 2 && y < clientRect.bottom - 2;
};
